import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment-timezone";
import { createPortal } from "react-dom";
import { Box, Label } from "theme-ui";

import { getRecordSelectedSnapshots } from "api/MulchMap";
import { themevals } from "theme/themevals";
import H5 from "components/typography/H5";
import P from "components/typography/P";

/**
 * SnapshotSelectionDisplay \u2014 Session 28 hotfix 11
 *
 * Read-only counterpart to SnapshotIncludePicker. Renders the ordered
 * list of snapshots that a company user has selected for a record\u2019s
 * client-facing PDF. Used on the Quote / Invoice view for clients and
 * on the PO view for providers so recipients can review the same
 * site-map imagery they\u2019ll get in the PDF, right in the app.
 *
 * Loads metadata + signed URLs in one call via
 * `GET /mulch-maps/records/:recordType/:recordId/selected-snapshots`,
 * which is access-checked server-side (works for company users,
 * clients, and providers).
 *
 * If there\u2019s no selection or the selection resolves to zero snapshots
 * (all deleted since selection), the component renders nothing.
 */

interface Props {
  recordType: "quote" | "invoice" | "purchase_order";
  recordId: string | null | undefined;
  /**
   * Section title. Defaults to \u201cSite maps included\u201d but callers can
   * override for context (\u201cSite maps in this quote\u201d, etc.).
   */
  heading?: string;
}

interface SnapshotRow {
  id: string;
  basemap: string | null;
  takenAt: string;
  signedUrl: string | null;
}

const BASEMAP_LABELS: Record<string, string> = {
  google_hybrid: "Google 2D",
  mapbox_sat: "Mapbox",
  esri_sat: "Esri",
  google_3d: "Google 3D",
};

const styles: Record<string, React.CSSProperties> = {
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
    gap: 12,
    marginTop: 12,
  },
  card: {
    borderRadius: 8,
    overflow: "hidden",
    background: "#F9FAFB",
    border: "0.5px solid " + themevals.colors.inputOutline,
    cursor: "pointer",
    transition: "border-color 0.15s ease",
  },
  thumb: {
    aspectRatio: "4 / 3",
    background: "#E4E7EB",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    color: "#78716C",
  },
  thumbImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  basemapPill: {
    position: "absolute",
    top: 6,
    left: 6,
    padding: "2px 6px",
    background: "rgba(17,17,17,0.75)",
    color: "#fff",
    borderRadius: 3,
    fontSize: 9,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    pointerEvents: "none",
  },
  cardFoot: {
    padding: "6px 8px",
    fontSize: 10,
    color: "#5D5E62",
    fontFamily: "Poppins, sans-serif",
  },
};

const SnapshotSelectionDisplay: React.FC<Props> = ({
  recordType,
  recordId,
  heading = "Site maps included",
}) => {
  const [rows, setRows] = useState<SnapshotRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!recordId) {
      setRows([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const result = await getRecordSelectedSnapshots(recordType, recordId);
      if (cancelled) return;
      if (Array.isArray(result)) {
        setRows(result);
      } else {
        // Access error or record not found \u2014 render nothing rather than
        // an error state; this component is best-effort.
        setRows([]);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [recordType, recordId]);

  const openLightbox = useCallback(
    (index: number) => {
      const row = rows[index];
      if (!row || !row.signedUrl) return;
      setLightboxIndex(index);
    },
    [rows],
  );

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const showPrev = useCallback(() => {
    setLightboxIndex((idx) => {
      if (idx === null) return null;
      return idx > 0 ? idx - 1 : rows.length - 1;
    });
  }, [rows.length]);

  const showNext = useCallback(() => {
    setLightboxIndex((idx) => {
      if (idx === null) return null;
      return idx < rows.length - 1 ? idx + 1 : 0;
    });
  }, [rows.length]);

  // Keyboard: Esc closes, arrows navigate.
  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") showPrev();
      else if (e.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex, closeLightbox, showPrev, showNext]);

  const basemapLabelFor = useMemo(
    () => (basemap: string | null | undefined) => {
      if (!basemap) return null;
      return BASEMAP_LABELS[basemap] || basemap;
    },
    [],
  );

  if (loading) return null;
  if (rows.length === 0) return null;

  return (
    <Box sx={{ marginTop: "16px", paddingRight: "30px" }}>
      <H5
        fontFamily={"Poppins"}
        color={themevals.colors.black}
        fontSize={"14px"}
        fontWeight={600}
        marginTop={"0px"}
        marginBottom={"4px"}
      >
        {heading}
      </H5>
      <Label
        sx={{
          fontFamily: "Poppins",
          fontSize: 12,
          color: themevals.colors.dkgrey,
        }}
      >
        {rows.length === 1 ? "1 site map" : rows.length + " site maps"}
        {" \u00b7 same order as the PDF"}
      </Label>

      <div style={styles.grid}>
        {rows.map((row, i) => {
          const label = basemapLabelFor(row.basemap);
          const taken = row.takenAt
            ? moment.utc(row.takenAt).local().format("MMM D \u00b7 h:mm a")
            : "";
          return (
            <div
              key={row.id}
              style={styles.card}
              onClick={() => openLightbox(i)}
              title="Click to view full size"
            >
              <div style={styles.thumb}>
                {row.signedUrl ? (
                  <img
                    src={row.signedUrl}
                    alt={"Site map " + taken}
                    style={styles.thumbImg}
                  />
                ) : (
                  <span>Loading{"\u2026"}</span>
                )}
                {label && <span style={styles.basemapPill}>{label}</span>}
              </div>
              <div style={styles.cardFoot}>
                <P
                  marginTop="0px"
                  marginBottom="0px"
                  fontFamily="Poppins"
                  fontSize="10px"
                  color={themevals.colors.dkgrey}
                >
                  {taken}
                </P>
              </div>
            </div>
          );
        })}
      </div>

      {lightboxIndex !== null &&
        rows[lightboxIndex] &&
        createPortal(
          <div
            onClick={closeLightbox}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.85)",
              zIndex: 2000,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: 32,
              cursor: "zoom-out",
            }}
          >
            {rows[lightboxIndex].signedUrl && (
              <img
                src={rows[lightboxIndex].signedUrl as string}
                alt="Site map full size"
                onClick={(e) => e.stopPropagation()}
                style={{
                  maxWidth: "92vw",
                  maxHeight: "84vh",
                  objectFit: "contain",
                  boxShadow: "0 8px 40px rgba(0,0,0,0.5)",
                  cursor: "default",
                }}
              />
            )}
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                marginTop: 16,
                color: "#fff",
                fontFamily: "Poppins, sans-serif",
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                gap: 16,
              }}
            >
              {basemapLabelFor(rows[lightboxIndex].basemap) && (
                <span
                  style={{
                    padding: "3px 8px",
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: 4,
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                  }}
                >
                  {basemapLabelFor(rows[lightboxIndex].basemap)}
                </span>
              )}
              <span>
                {rows[lightboxIndex].takenAt
                  ? moment
                      .utc(rows[lightboxIndex].takenAt)
                      .local()
                      .format("MMM D, YYYY \u00b7 h:mm a")
                  : ""}
              </span>
              <span style={{ color: "#B8B8B8" }}>
                {lightboxIndex + 1} of {rows.length}
              </span>
            </div>
            {rows.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    showPrev();
                  }}
                  aria-label="Previous site map"
                  style={{
                    position: "absolute",
                    left: 24,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "rgba(255,255,255,0.15)",
                    border: "none",
                    color: "#fff",
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    cursor: "pointer",
                    fontSize: 22,
                  }}
                >
                  {"\u2039"}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    showNext();
                  }}
                  aria-label="Next site map"
                  style={{
                    position: "absolute",
                    right: 24,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "rgba(255,255,255,0.15)",
                    border: "none",
                    color: "#fff",
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    cursor: "pointer",
                    fontSize: 22,
                  }}
                >
                  {"\u203A"}
                </button>
              </>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                closeLightbox();
              }}
              aria-label="Close"
              style={{
                position: "absolute",
                top: 24,
                right: 24,
                background: "rgba(255,255,255,0.15)",
                border: "none",
                color: "#fff",
                width: 40,
                height: 40,
                borderRadius: "50%",
                cursor: "pointer",
                fontSize: 20,
              }}
            >
              {"\u00d7"}
            </button>
          </div>,
          document.body,
        )}
    </Box>
  );
};

export default SnapshotSelectionDisplay;
